import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, query, where, doc, updateDoc, limit, startAfter, getDoc } from 'firebase/firestore';
import { useLocation } from 'react-router-dom';
import '../style/ReturnOrder.css';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';


const ReturnOrder = () => {
    const [returnOrders, setReturnOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [trackingLoading, setTrackingLoading] = useState({});
    
    // Pagination state
    const [lastVisible, setLastVisible] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const PAGE_SIZE = 10;
    
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const shopId = queryParams.get('shop');

    const user = auth.currentUser;
    if (!user) {
        throw new Error("User not logged in");
    }

    // Fetch return orders from Firestore with pagination and sorting by date
    const fetchReturnOrders = async (isLoadMore = false) => {
        try {
            if (isLoadMore) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }
            
            const returnsCollection = collection(db, 'users', user.uid, 'return_orders');
            let q;
            
            if (isLoadMore && lastVisible) {
                // Fetch next batch of orders
                q = query(
                    returnsCollection,
                    // orderBy('return_date', 'desc'), // Sort by return_date desc (newest first)
                    startAfter(lastVisible),
                    limit(PAGE_SIZE)
                );
            } else {
                // Fetch first batch of orders
                q = query(
                    returnsCollection,
                    // orderBy('return_date', 'desc'), // Sort by return_date desc (newest first)
                    limit(PAGE_SIZE)
                );
            }
            
            const ordersSnapshot = await getDocs(q);
            
            // If we got fewer results than the page size, there are no more results
            if (ordersSnapshot.docs.length < PAGE_SIZE) {
                setHasMore(false);
            } else {
                setHasMore(true);
            }
            
            // Set last document for pagination
            if (ordersSnapshot.docs.length > 0) {
                setLastVisible(ordersSnapshot.docs[ordersSnapshot.docs.length - 1]);
            }
            
            const ordersList = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            if (isLoadMore) {
                // Append to existing orders
                setReturnOrders(prevOrders => [...prevOrders, ...ordersList]);
            } else {
                // Replace all orders
                setReturnOrders(ordersList);
            }
            
            // Initialize filtered orders with all return orders
            if (!isLoadMore) {
                setFilteredOrders(ordersList);
            } else {
                setFilteredOrders(prevFiltered => [...prevFiltered, ...ordersList]);
            }
        } catch (error) {
            console.error("Error fetching return orders:", error);
            alert("Error loading return orders. Please try again.");
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // Search for return order by ID directly in Firestore
    const searchOrderById = async (searchId) => {
        try {
            setIsSearching(true);
            
            // Search by order ID (document ID)
            const returnsCollection = collection(db, 'users', user.uid, 'return_orders');
            let results = [];
            
            // Search for direct match on document ID
            if (searchId.trim()) {
                try {
                    const orderDoc = doc(db, 'users', user.uid, 'return_orders', searchId.trim());
                    const orderSnapshot = await getDocs(query(
                        returnsCollection,
                        where('__name__', '==', searchId.trim())
                    ));
                    
                    if (!orderSnapshot.empty) {
                        const orderData = orderSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                        results = [...results, ...orderData];
                    }
                } catch (err) {
                    console.log("Error searching by document ID:", err);
                }
                
                // Search by original order ID
                try {
                    const orderIdSnapshot = await getDocs(query(
                        returnsCollection,
                        where('order_id', '==', searchId.trim())
                    ));
                    
                    if (!orderIdSnapshot.empty) {
                        const orderData = orderIdSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                        results = [...results, ...orderData];
                    }
                } catch (err) {
                    console.log("Error searching by order ID:", err);
                }
                
                // If exact matches didn't find anything, try contains for order ID
                if (results.length === 0) {
                    try {
                        // For contains queries, we need to get all documents and filter client-side
                        const allOrdersSnapshot = await getDocs(query(returnsCollection));
                        
                        const containsMatches = allOrdersSnapshot.docs
                            .map(doc => ({ id: doc.id, ...doc.data() }))
                            .filter(order => 
                                (order.order_id && order.order_id.includes(searchId.trim())) ||
                                order.id.includes(searchId.trim())
                            );
                        
                        results = [...results, ...containsMatches];
                    } catch (err) {
                        console.log("Error performing contains search:", err);
                    }
                }
            }
            
            setFilteredOrders(results);
            setIsSearching(false);
        } catch (error) {
            console.error("Error searching for return order:", error);
            setIsSearching(false);
        }
    };


    const getAwbNumber = async (orderId) => {
        try {
            console.log("Fetching AWB for order:", orderId);
            console.log("User UID:", user.uid);
            
            // Firebase v9 syntax - using doc() and getDoc()
            const confirmedOrderDocRef = doc(db, 'users', user.uid, 'confirm_return_order', orderId);
            const confirmedOrderDoc = await getDoc(confirmedOrderDocRef);
            
            console.log("Document exists:", confirmedOrderDoc.exists());
            
            if (confirmedOrderDoc.exists()) {
                const orderData = confirmedOrderDoc.data();
                console.log("Order data:", orderData);
                console.log("AWB Number:", orderData.awbNumber);
                return orderData.courier_assignment.data.awb_number || null;
            } else {
                console.error("No confirmed_orders document found for order ID:", orderId);
                return null;
            }
        } catch (error) {
            console.error("Error fetching AWB number from confirmed_orders:", error);
            return null;
        }
    };


    const handleTrackOrder = async (orderId, e) => {
    e.stopPropagation(); // Prevent opening the modal when clicking on tracking button
    
    try {
        setTrackingLoading(prev => ({ ...prev, [orderId]: true }));
        
        // First, try the original tracking method
        console.log("Attempting original tracking method for order:", orderId);
        const originalTrackingResult = await fetchTrackingData(orderId);
        
        // Check if original tracking was successful
        if (originalTrackingResult && originalTrackingResult.success) {
            console.log("Original tracking successful");
            return; // Exit early if original tracking worked
        }
        
        // If original tracking failed, try AWB tracking method
        console.log("Original tracking failed, attempting AWB tracking method");
        
        // Get AWB number from confirmed_order Firestore document
        const awbNumber = await getAwbNumber(orderId);
        
        if (awbNumber) {
            const shipmozoTrackingUrl = `https://panel.shipmozo.com/track-order?awb=${awbNumber}`;
            
            // Update the order with tracking URL in local state
            setReturnOrders(prevOrders => 
                prevOrders.map(order => 
                    order.id === orderId 
                        ? { ...order, tracking_url: shipmozoTrackingUrl } 
                        : order
                )
            );
            
            // Also update filtered orders
            setFilteredOrders(prevFiltered => 
                prevFiltered.map(order => 
                    order.id === orderId 
                        ? { ...order, tracking_url: shipmozoTrackingUrl } 
                        : order
                )
            );
            
            // Open the Shipmozo tracking URL in a new tab
            window.open(shipmozoTrackingUrl, '_blank');
            console.log("AWB tracking successful");
        } else {
            alert("Tracking information not available for this order.");
            console.error("Both tracking methods failed for order:", orderId);
        }
        
    } catch (error) {
        console.error("Error in handleTrackOrder for order", orderId, error);
        alert("Failed to fetch tracking information. Please try again.");
    } finally {
        setTrackingLoading(prev => ({ ...prev, [orderId]: false }));
    }
};
    
const fetchTrackingData = async (orderId) => {
    try {
        const response = await axios.get(`https://ufb-1.onrender.com/api/track-order?order_id=${orderId}`);
        
        if (response.data && response.data.tracking_url) {
            // Update the order with tracking URL in local state
            setReturnOrders(prevOrders => 
                prevOrders.map(order => 
                    order.id === orderId 
                        ? { ...order, tracking_url: response.data.tracking_url } 
                        : order
                )
            );
            
            // Also update filtered orders
            setFilteredOrders(prevFiltered => 
                prevFiltered.map(order => 
                    order.id === orderId 
                        ? { ...order, tracking_url: response.data.tracking_url } 
                        : order
                )
            );
            
            // Open the tracking URL in a new tab
            window.open(response.data.tracking_url, '_blank');
            
            return {
                success: true,
                tracking_url: response.data.tracking_url,
            };
        } else {
            return {
                success: false,
                tracking_url: null,
            };
        }
    } catch (error) {
        console.error("Error fetching tracking data for order", orderId, error);
        return {
            success: false,
            tracking_url: null,
        };
    }
};

    //  const fetchTrackingData = async (orderId) => {
    //         try {
    //             setTrackingLoading(prev => ({ ...prev, [orderId]: true }));
                
    //             const response = await axios.get(`https://ufb-1.onrender.com/api/track-order?order_id=${orderId}`);
                
    //             if (response.data && response.data.tracking_url) {
    //                 // Update the order with tracking URL in local state
    //                 setReturnOrders(prevOrders => 
    //                     prevOrders.map(order => 
    //                         order.id === orderId 
    //                             ? { ...order, tracking_url: response.data.tracking_url } 
    //                             : order
    //                     )
    //                 );
                    
    //                 // Also update filtered orders
    //                 setFilteredOrders(prevFiltered => 
    //                     prevFiltered.map(order => 
    //                         order.id === orderId 
    //                             ? { ...order, tracking_url: response.data.tracking_url } 
    //                             : order
    //                     )
    //                 );
                    
    //                 // Open the tracking URL in a new tab
    //                 window.open(response.data.tracking_url, '_blank');
                    
    //                 return {
    //                     tracking_url: response.data.tracking_url,
    //                 };
    //             }
    //         } catch (error) {
    //             console.error("Error fetching tracking data for order", orderId, error);
    //             alert("Failed to fetch tracking information. Please try again.");
    //         } finally {
    //             setTrackingLoading(prev => ({ ...prev, [orderId]: false }));
    //         }
    //         return { tracking_url: null };
    //     };


    // const handleTrackOrder = async (orderId, e) => {
    //     e.stopPropagation(); // Prevent opening the modal when clicking on tracking button
    //     await fetchTrackingData(orderId);
    // };

    

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        
        if (value.trim() === '') {
            // If search is cleared, show the loaded orders
            setFilteredOrders(returnOrders);
        } else {
            // When user is typing, first filter locally
            const localFiltered = returnOrders.filter(order => 
                order.id.includes(value) || 
                (order.order_id && order.order_id.includes(value))
            );
            
            setFilteredOrders(localFiltered);
            
            // If we have a search term of at least 3 characters, search Firestore
            if (value.trim().length >= 3) {
                // Use debounce to avoid too many Firestore queries
                const timeoutId = setTimeout(() => {
                    searchOrderById(value);
                }, 500);
                
                return () => clearTimeout(timeoutId);
            }
        }
    };

    // Load more orders
    const handleLoadMore = () => {
        if (hasMore && !loadingMore) {
            fetchReturnOrders(true);
        }
    };

    // Handle invoice download
    const handleDownloadInvoice = (invoiceUrl) => {
        if (invoiceUrl) {
            window.open(invoiceUrl, '_blank');
        } else {
            alert('Invoice not available for this return order.');
        }
    };

    // Load return orders on component mount
    useEffect(() => {
        setLastVisible(null);
        setHasMore(true);
        fetchReturnOrders();
    }, [shopId]);

    // Filter orders based on date range
    useEffect(() => {
        let filtered = returnOrders;
        
        // Only filter by search term if it's not actively searching in Firestore
        if (searchTerm && !isSearching) {
            filtered = filtered.filter(order => 
                order.id.includes(searchTerm) || 
                (order.order_id && order.order_id.includes(searchTerm))
            );
        }
        
        // Filter by date range
        if (startDate && endDate) {
            filtered = filtered.filter(order => {
                if (!order.order_date) return false;
                
                const orderDate = new Date(order.order_date);
                const start = new Date(startDate);
                const end = new Date(endDate);
                
                // Set end date to end of day
                end.setHours(23, 59, 59, 999);
                
                return orderDate >= start && orderDate <= end;
            });
        }
        
        // Only update filtered orders if not actively searching
        if (!isSearching || searchTerm.trim() === '') {
            setFilteredOrders(filtered);
        }
    }, [startDate, endDate, returnOrders]);

    // Update filtered orders when search term changes, but only for local filtering
    useEffect(() => {
        if (!isSearching && searchTerm.trim() === '') {
            setFilteredOrders(returnOrders);
        }
    }, [searchTerm, isSearching, returnOrders]);

    if (loading && !loadingMore && !isSearching) {
        return <div>Loading return orders...</div>;
    }

    return (
        <div className="orders-page">
            <Header />
            <div className="orders-container">
                <Sidebar />
                <div className="main-content">
                    <h1>Return Orders</h1>
                    
                    <div className="filters-container">
                        <input 
                            type="text" 
                            placeholder="Search by Return ID or Original Order ID" 
                            value={searchTerm} 
                            onChange={handleSearchChange} 
                            className="search-bar"
                        />
                        
                        <div className="date-filters">
                            <div className="date-filter">
                                <label>From Date:</label>
                                <input 
                                    type="date" 
                                    value={startDate} 
                                    onChange={(e) => setStartDate(e.target.value)} 
                                />
                            </div>
                            
                            <div className="date-filter">
                                <label>To Date:</label>
                                <input 
                                    type="date" 
                                    value={endDate} 
                                    onChange={(e) => setEndDate(e.target.value)} 
                                />
                            </div>
                        </div>
                    </div>
                    
                    {isSearching && (
                        <div style={{ textAlign: 'center', margin: '20px 0' }}>
                            Searching for return orders...
                        </div>
                    )}
                    
                    {filteredOrders.length > 0 ? (
                        <table className="orders-table">
                            <thead>
                                <tr>
                                    <th>Return ID</th>
                                    {/* <th>Original Order ID</th> */}
                                    <th>Return Date</th>
                                    <th>Items</th>
                                    {/* <th>Quantity</th> */}
                                    <th>Invoice</th>
                                    <th>Tracking Status</th>
                                    <th>Track Order</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredOrders.map((order) => (
                                    <tr key={order.id}>
                                        <td>{order.id}</td>
                                        {/* <td>{order.order_id || 'NA'}</td> */}
                                        <td>{order.order_date ? new Date(order.order_date).toLocaleDateString() : 'NA'}</td>
                                        <td>
                                            {order.order_items?.map((item, index) => (
                                                <p key={index}>{item.name} ({item.sku})</p>
                                            ))}
                                        </td>
                                        {/* <td>
                                            {order.order_items?.map((item, index) => (
                                                <p key={index}>{item.units}</p>
                                            ))}
                                        </td> */}
                                        <td>
                                            {order.invoiceUrl ? (
                                                <button 
                                                    onClick={() => handleDownloadInvoice(order.invoiceUrl)} 
                                                    className="download-invoice-btn"
                                                >
                                                    Download Invoice
                                                </button>
                                            ) : (
                                                <span>No Invoice</span>
                                            )}
                                        </td>

                                        <td>{order.tracking_status}</td>
                                        <td>
                                        <button
                                                onClick={(e) => handleTrackOrder(order.id, e)}
                                                disabled={trackingLoading[order.id]}
                                                className="track-order-btn"
                                                // style={{
                                                //     padding: '6px 12px',
                                                //     backgroundColor: '#4a90e2',
                                                //     color: 'white',
                                                //     border: 'none',
                                                //     borderRadius: '4px',
                                                //     cursor: trackingLoading[order.id] ? 'not-allowed' : 'pointer'
                                                // }}
                                            >
                                                {trackingLoading[order.id] ? 'Loading...' : 'Track Order'}
                                            </button>

                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="no-orders-message">
                            {isSearching ? 'Searching...' : 'No return orders found.'}
                        </div>
                    )}
                    
                    {/* Load More Button - only show when not searching or filtering */}
                    {!searchTerm && !startDate && !endDate && hasMore && (
                        <div className="load-more-container" style={{ textAlign: 'center', margin: '20px 0' }}>
                            <button 
                                className="load-more-btn" 
                                onClick={handleLoadMore}
                                disabled={loadingMore}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#4a90e2',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: loadingMore ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {loadingMore ? 'Loading...' : 'Load More Return Orders'}
                            </button>
                        </div>
                    )}
                    
                    {loadingMore && (
                        <div style={{ textAlign: 'center', margin: '10px 0' }}>
                            Loading more return orders...
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReturnOrder;